import {useEffect, useMemo} from 'react'
import type {HeadersArgs, LoaderFunctionArgs, MetaFunction} from 'react-router'
import {useLoaderData} from 'react-router'
import {json} from '@core/lib/json'
import {mergeMeta} from '@core/lib/mergeMeta'
import {getCatalogView, getUnreleasedSlugs} from '@product/narcis/packs/catalog'

import {getSession} from '@core/auth/session.server'
import {getProductId} from '@core/products/registry'
import {readWallPage, toWallResponse} from '@core/wall/tiles.server'
import {leanOfSession} from '@core/wall/touches.server'
import type {FacetOffer} from '@core/wall/facetOffer'
import {parseProfileKey} from '@core/wall/profile'
import {NARCIS_FACETS} from '@product/narcis/wall/facets'
import {readMineResponse} from '@core/wall/mine.server'
import {readWallQueryParam} from '@core/wall/tsQuery'
import {readWallScope} from '@core/wall/pageVerb'
import type {MineResponse, WallResponse} from '@core/wall/contract'
import {Wall} from '@core/wall/ui/Wall'
import {SignInLayer} from '@core/auth-ui/SignInLayer/SignInLayer'
import {NARCIS_SIGN_IN_CARD} from '@product/narcis/wall/signInCard'
import {narcisThemeFor} from '@product/narcis/wall/theme'
import {NarcisWallHeader} from '@product/narcis/wall/header'
import {readWallWidthCookie} from '@core/wall/ssrWidth'
import {resolveMineIdentity} from '@product/narcis/wall/identity.server'
import {intakePath, PAID_PARAM} from '@product/narcis/path'
import {PAYMENT_RECEIVED_NOTICE} from '@product/narcis/proposition'
import {HomeDoorstep} from '@core/wall/home/HomeDoorstep'
import {homeMeta} from '@core/wall/home/doorstep'
import {narcisHome, narcisHouse, type NarcisEndpaperData} from '@product/narcis/wall/home'

function wallSignInLayer({redirectTo, trigger, onClose}: {redirectTo: string; trigger: string; onClose: () => void}) {
    return <SignInLayer redirectTo={redirectTo} trigger={trigger} onClose={onClose} card={NARCIS_SIGN_IN_CARD} />
}

/** The endpaper's data: a registry read, so it survives a database failure. */
async function loadEndpaperData(): Promise<NarcisEndpaperData> {
    const {livePacks} = getCatalogView()
    const {groupPacksBySeries} = await import('@core/packs/series/classify.server')
    const {NARCIS_SERIES, NARCIS_RESIDUE} = await import('@core/packs/series/narcisSeries.server')
    const cards = livePacks.map((p) => ({slug: p.slug, name: p.name}))
    return {
        totalPacks: livePacks.length,
        packTeaser: cards.slice(0, 24),
        packIndexGroups: groupPacksBySeries(cards, NARCIS_SERIES, NARCIS_RESIDUE),
    }
}

const NO_ENDPAPER: NarcisEndpaperData = {totalPacks: 0, packTeaser: [], packIndexGroups: []}

/** The home SSR loader — `narcis.ai/` is the wall, unconditionally (narcis wall plan step 7). */
export async function loader({request}: LoaderFunctionArgs) {
    const product = getProductId()
    const url = new URL(request.url)
    const q = readWallQueryParam(url.searchParams)
    // A pack page's arrival names its pack: that pack's tiles lead the search (`withPackFirst`).
    const {pack} = readWallScope(url.searchParams)
    const ssrContainerWidth = readWallWidthCookie(request.headers.get('cookie'))
    const session = await getSession(request.headers.get('cookie'))
    const userId = session.get('userId') as string | undefined
    // The landing after a payment (`path.ts`): the wall says so once.
    const arrival = userId && url.searchParams.get(PAID_PARAM) === '1' ? PAYMENT_RECEIVED_NOTICE : null

    // The doorstep is signed-out only; a signed-in request skips its read.
    const endpaper = userId ? NO_ENDPAPER : await loadEndpaperData()
    // Scheduled packs: their tiles show, their labels do not link (`narcisThemeFor`).
    const unreleasedSlugs = getUnreleasedSlugs()

    // One assembly for the whole home read (wall review follow-ups unit 3, `thinking/plans/
    // 2026_09_23_wall_review_followups.md`): `readMineResponse` resolves the identity alongside
    // the rows, so a failure anywhere in either read — wall page, mine rows, or identity — falls
    // through to the SAME empty-wall/mine-null response the SSR page must always render something
    // for. This is a deliberate narrowing from the old two-catch shape: an identity-resolution
    // failure used to default to `{kind: 'model', state: 'missing', ...}` while still showing the
    // wall and mine rows; it now blanks the same as any other read failure in this call.
    let wallPageRead: Awaited<ReturnType<typeof readWallPage>>
    let mine: MineResponse | null
    let profile = ''
    let personal = false
    let facetOffer: FacetOffer = {}
    try {
        ;({profile, personal, facetOffer} = await leanOfSession(session, product, NARCIS_FACETS, new Date()))
        ;[wallPageRead, mine] = await Promise.all([
            readWallPage({product, q, cursor: null, profile: parseProfileKey(profile), pack: pack ?? undefined}),
            userId ? readMineResponse({product, owner: {kind: 'user', userId}, q, resolveMineIdentity}) : Promise.resolve(null),
        ])
    } catch (err) {
        const {logger} = await import('@core/utils/logger.server')
        logger.error('[wall][narcis] home SSR loader failed', {
            q, userId,
            error: err,
        })
        return json(
            {
                wallPage: {tiles: [], next_cursor: null, seam: false} as WallResponse,
                profile: '',
                facetOffer: {} as FacetOffer,
                mine: null,
                signedIn: !!userId,
                ssrContainerWidth,
                searched: !!q,
                endpaper,
                unreleasedSlugs,
                arrival,
            },
            {headers: {'Cache-Control': 'no-store'}},
        )
    }

    const wallPage = toWallResponse(wallPageRead)

    const signedIn = !!userId
    const cacheControl = signedIn || personal ? 'private, no-store' : q ? 'public, max-age=60, s-maxage=60' : undefined

    return json(
        {wallPage, mine, signedIn, ssrContainerWidth, searched: !!q, endpaper, unreleasedSlugs, profile, facetOffer, arrival},
        cacheControl ? {headers: {'Cache-Control': cacheControl}} : undefined,
    )
}

/* -------------------------------------------------------------------------- */
/*                                 Meta / SEO                                 */
/* -------------------------------------------------------------------------- */

export const meta: MetaFunction<typeof loader> = ({matches}) => mergeMeta(matches, homeMeta(narcisHouse))

/** React Router 7 propagates only `Set-Cookie` from a loader Response onto the document. */
export function headers({loaderHeaders}: HeadersArgs) {
    const cacheControl = loaderHeaders.get('Cache-Control')
    return cacheControl ? {'Cache-Control': cacheControl} : {}
}

/* -------------------------------------------------------------------------- */
/*                                   Component                                    */
/* -------------------------------------------------------------------------- */

export default function Home() {
    const {wallPage, mine, signedIn, ssrContainerWidth, searched, endpaper, unreleasedSlugs, profile, facetOffer, arrival} = useLoaderData<typeof loader>()
    // Keyed on the joined list so a revalidation carrying the same slugs keeps the theme's identity.
    const unreleasedKey = unreleasedSlugs.join(',')
    const theme = useMemo(() => narcisThemeFor(unreleasedKey ? unreleasedKey.split(',') : []), [unreleasedKey])

    // Said once: the marker leaves the address after the wall has read it (the wall's own effects run first).
    useEffect(() => {
        if (!arrival) return
        const params = new URLSearchParams(window.location.search)
        params.delete(PAID_PARAM)
        const qs = params.toString()
        window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? `?${qs}` : ''))
    }, [arrival])

    return (
        <>
            <Wall
                theme={theme}
                initialWallPage={wallPage}
                profile={profile ?? ''}
                facetOffer={facetOffer}
                initialMine={mine}
                signedIn={signedIn}
                ssrContainerWidth={ssrContainerWidth}
                renderHeader={(search) => <NarcisWallHeader search={search} />}
                renderSignIn={wallSignInLayer}
                intakeHref={intakePath}
                arrivalNotice={arrival}
            />

            {/* A `?q=` page canonicalises to `/`: its tiles are not the home's gallery. */}
            <HomeDoorstep house={narcisHome(endpaper)} anon={!signedIn} gallery={searched ? [] : wallPage.tiles} />
        </>
    )
}
